import FormControlledInput from '../../../components/inputs/FormInputs/FormControlledInput';
import FormControlledNumberInput from '../../../components/inputs/FormInputs/FormControlledNumberInput';
import FormControlledCalendar from '../../../components/inputs/FormInputs/FormControlledCalendar';
import FormControlledInputArea from '../../../components/inputs/FormInputs/FormControlledInputArea';
import FormControlledDropdown from '../../../components/inputs/FormInputs/FormControlledDropdown';
import DropdownStyled from '../../../components/inputs/StyledInputs/DropdownStyled';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { useState } from 'react';
import { PolicyForm } from '../../../Interfaces/Insurance/Policy';
import SingleFileInput from '../../../components/inputs/StyledInputs/SingleFileInput';
import { FILE_SIZE_LIMIT } from '@/src/utils/fileConsts';
import { transformBToMB } from '@/src/utils/fileUtil';
import { BILLING_TYPES, POLICY_TYPES } from './const';
import FormErrorMessage from '../../../components/messages/FormErrorMessage';

type PoliciesForm = {
  prevAttachment?: string;
  policyTypeDefaultValue?: string;
};

const PoliciesForm = ({
  prevAttachment,
  policyTypeDefaultValue,
}: PoliciesForm) => {
  const [showPolicyInput, setShowPolicyInput] = useState(
    policyTypeDefaultValue
      ? !POLICY_TYPES.includes(policyTypeDefaultValue)
      : false
  );
  const [policyType, setPolicyType] = useState(
    !policyTypeDefaultValue
      ? null
      : !POLICY_TYPES.includes(policyTypeDefaultValue)
      ? POLICY_TYPES[POLICY_TYPES.length - 1]
      : policyTypeDefaultValue
  );
  const {
    formState: { errors },
    setValue,
    resetField,
    control,
  } = useFormContext<PolicyForm>();
  const removed = useWatch<PolicyForm>({ name: 'removeAttachment' });

  return (
    <div className="grid mx-3 justify-content-center">
      <div className="col-12 sm:col-6">
        <label htmlFor="policyType" className="text-standard block mb-2">
          Policy
        </label>
        <DropdownStyled
          id="policyType"
          options={POLICY_TYPES}
          value={policyType}
          placeholder={'Select Policy'}
          error={!policyType && !!errors['policy']}
          onChange={(e) => {
            const value = e.value;

            setPolicyType(value);
            if (value !== POLICY_TYPES[POLICY_TYPES.length - 1]) {
              setShowPolicyInput(false);
              setValue('policy', value);
            } else {
              resetField('policy');
              setShowPolicyInput(true);
            }
          }}
        />
        {!policyType && errors['policy'] && (
          <FormErrorMessage errorMessage={errors['policy'].message as string} />
        )}
      </div>
      <div
        className={`col-12 sm:col-6 ${
          !showPolicyInput && 'visibility-hidden'
        } `}
      >
        <label htmlFor="policy" className="text-standard block mb-2">
          New Policy Type
        </label>
        <div className="mt-auto w-full">
          <FormControlledInput
            defaultValue=""
            formID="policy"
            rules={{
              required: {
                value: true,
                message: 'Policy is a required field',
              },
            }}
            autocomplete="off"
          />
        </div>
      </div>
      <div className="col-12 sm:col-6">
        <label htmlFor="limit" className="text-standard block mb-2">
          Limit
        </label>
        <FormControlledNumberInput
          formID="limit"
          min={0}
          mode="currency"
          currency="USD"
          locale="en-US"
          rules={{
            required: {
              value: true,
              message: 'Limit is a required field',
            },
          }}
        />
      </div>
      <div className="col-12 sm:col-6">
        <label htmlFor="deductible" className="text-standard block mb-2">
          Deductible / Retention
        </label>
        <FormControlledNumberInput
          formID="deductible"
          min={0}
          mode="currency"
          currency="USD"
          locale="en-US"
        />
      </div>
      <div className="col-12 sm:col-6">
        <label htmlFor="renewal" className="text-standard block mb-2">
          Renewal
        </label>
        <FormControlledNumberInput
          formID="renewal"
          min={0}
          mode="currency"
          currency="USD"
          locale="en-US"
        />
      </div>
      <div className="col-12 sm:col-6">
        <label htmlFor="insurer" className="text-standard block mb-2">
          Insurer
        </label>
        <FormControlledInput
          defaultValue=""
          formID="insurer"
          autocomplete="off"
        />
      </div>
      <div className="col-12 sm:col-6">
        <label htmlFor="termStart" className="text-standard block mb-2">
          Term Start
        </label>
        <FormControlledCalendar
          formID="termStart"
          readOnlyInput
          showButtonBar
          className="w-full"
          rules={{
            required: {
              value: true,
              message: 'Term Start is a required field',
            },
          }}
        />
      </div>
      <div className="col-12 sm:col-6">
        <label htmlFor="termEnd" className="text-standard block mb-2">
          Term End
        </label>
        <FormControlledCalendar
          formID="termEnd"
          readOnlyInput
          showButtonBar
          className="w-full"
          rules={{
            required: {
              value: true,
              message: 'Term End is a required field',
            },
          }}
        />
      </div>
      <div className="col-12 sm:col-6">
        <label htmlFor="number" className="text-standard block mb-2">
          Policy Number
        </label>
        <FormControlledInput
          defaultValue=""
          formID="number"
          autocomplete="off"
        />
      </div>
      <div className="col-12 sm:col-6">
        <label htmlFor="billing" className="text-standard block mb-2">
          Billing
        </label>
        <FormControlledDropdown
          formID="billing"
          options={BILLING_TYPES}
          placeholder={'Select Billing'}
          clearable={true}
        />
      </div>
      <div className="col-12">
        <label htmlFor="attachment" className="text-standard block mb-2">
          Attachment
        </label>
        <div>
          {prevAttachment && !removed && (
            <div>
              <div className="flex">
                The current attachment is:
                <div className="flex flex-column gap-2">
                  <span className="flex">
                    <span className="ml-1 mr-2">{prevAttachment}</span>
                    <i
                      onClick={() => {
                        setValue('removeAttachment', true);
                      }}
                      className={`pi pi-ban errorText cursor-pointer my-auto`}
                    />
                  </span>
                </div>
              </div>
              <div className="mt-2 mb-1">
                To add a new attachment select it below:
              </div>
            </div>
          )}
          <Controller
            name="attachment"
            aria-label="file-input"
            control={control}
            render={({ field: { onChange, value } }) => (
              <div>
                <SingleFileInput
                  value={value}
                  onChange={onChange}
                  acceptedFormats={['.pdf']}
                  totalLimit={FILE_SIZE_LIMIT}
                  totalLimitLabel={`${transformBToMB(FILE_SIZE_LIMIT)} MB`}
                  emptyMessage={`Upload attachment file. Allowed extensions: ${[
                    '.pdf',
                  ].join(', ')}.`}
                />
              </div>
            )}
          />
        </div>
      </div>
      <div className="col-12">
        <label htmlFor="notes" className="text-standard block mb-2">
          Notes
        </label>
        <FormControlledInputArea
          defaultValue=""
          formID="notes"
          rows={5}
          cols={60}
        />
      </div>
    </div>
  );
};

export default PoliciesForm;
